import { Link } from '@tanstack/react-router';
import { ImageOff } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import type { SocialTopProduct } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Card, CardHeader, CardTitle } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

export interface TopProductsTableProps {
  items: readonly SocialTopProduct[] | undefined;
  isLoading: boolean;
}

/** Products the network showed most: Nucleus posts and catalogue Product Pins matched by link. */
export function TopProductsTable({ items, isLoading }: TopProductsTableProps) {
  const { t } = useTranslation('social');
  const format = useFormat();

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardHeader className="flex flex-col gap-0.5 border-b px-5 py-4">
        <CardTitle className="text-[15px]">{t('analytics.topProducts.title')}</CardTitle>
        <p className="text-muted-foreground text-xs">{t('analytics.topProducts.hint')}</p>
      </CardHeader>
      <Table aria-label={t('analytics.topProducts.title')}>
        <TableHeader>
          <TableRow>
            <TableHead className="pl-5">{t('analytics.topProducts.columns.product')}</TableHead>
            <TableHead className="text-right">
              {t('analytics.topProducts.columns.impressions')}
            </TableHead>
            <TableHead className="text-right">
              {t('analytics.topProducts.columns.outboundClicks')}
            </TableHead>
            <TableHead className="pr-5 text-right">
              {t('analytics.topProducts.columns.orders')}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>
                <TableCell colSpan={4} className="px-5">
                  <Skeleton className="h-9" />
                </TableCell>
              </TableRow>
            ))
          ) : items && items.length > 0 ? (
            items.map((item) => (
              <TableRow key={item.productId}>
                <TableCell className="max-w-0 pl-5">
                  <div className="flex items-center gap-3">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt=""
                        className="bg-muted size-9 shrink-0 rounded-md object-cover"
                      />
                    ) : (
                      <span
                        className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md"
                        aria-hidden
                      >
                        <ImageOff className="size-4" />
                      </span>
                    )}
                    <Link
                      to="/org/products/$productId"
                      params={{ productId: item.productId }}
                      className="truncate font-medium hover:underline"
                    >
                      {item.title}
                    </Link>
                  </div>
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {format.number(item.impressions)}
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {format.number(item.outboundClicks)}
                </TableCell>
                <TableCell className="pr-5 text-right tabular-nums">
                  {format.number(item.orders)}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} className="text-muted-foreground h-20 text-center">
                {t('analytics.topProducts.empty')}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Card>
  );
}
